import React, { useMemo, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCodeCompare } from '@fortawesome/free-solid-svg-icons';
import AppChart, { ChartSeries } from 'shared/components/Chart/AppChart';
import ReportDataTable, {
  ReportColumn,
} from 'shared/components/Table/ReportDataTable';
import DrillBreadcrumb from 'shared/components/Dashboard/DrillBreadcrumb';
import {
  drillHint,
  drillLevelLabel,
  drillSearchPlaceholder,
  // Aliased: this component already has a `scopeLabel` PROP (the comparison
  // kind, e.g. "District Comparison"), which is a different thing.
  scopeLabel as geoScopeLabel,
} from 'shared/json/drilldown';

import './Comparison.scss';

/** A per-entity row from the API drill (district / block / school level). */
interface ComparisonRow {
  name: string;
  schools?: number;
  boys: number;
  girls: number;
  students: number;
  teachers: number;
  [key: string]: unknown;
}

/** Selectable ranking metrics — raw row counts plus derived indicators. */
type MetricKey =
  'students' | 'schools' | 'teachers' | 'gpt' | 'ptr' | 'ass' | 'gs';

export interface ComparisonProps {
  /** API-driven rows for the current drill level (from Dashboard reportView). */
  rows?: ComparisonRow[];
  /** Entity level: 1 = districts, 2 = blocks, 3 = schools. */
  level?: 1 | 2 | 3;
  /** Selected academic-year label, e.g. "2024-25". */
  year?: string;
  /** Context title, e.g. "District Comparison" / "Block Comparison …". */
  scopeLabel?: string;
  /** Selected geography NAMES — for the "currently viewing" context bar. */
  district?: string | null;
  block?: string | null;
  /**
   * Header-selected school. When set (at level 3), the ranked bar + indicator
   * table narrow to just that one school so the chart stays in sync with the
   * selection.
   */
  school?: string | null;
  schoolId?: number | null;
  /** Row click → drill down (district → blocks). */
  onRowClick?: (row: Record<string, unknown>) => void;
  /** Header setters so the "Back" bar can step up one level. */
  onDistrictChange?: (value: string) => void;
  onBlockChange?: (value: string) => void;
}

/**
 * Comparison view: entities ranked by a chosen metric (horizontal bar, above
 * the average in blue, below in orange) + an indicator table (GPI, PTR,
 * Avg/School, Girls %). API data only — the rows come from the Dashboard's
 * drill view, so selecting a district shows its blocks, a block its schools.
 */
const Comparison: React.FC<ComparisonProps> = ({
  rows = [],
  level = 1,
  year = '',
  scopeLabel = 'District Comparison',
  district = null,
  block = null,
  school = null,
  schoolId = null,
  onRowClick,
  onDistrictChange,
  onBlockChange,
}) => {
  const metrics = [
    { label: 'Total Students', value: 'students' },
    { label: 'Total Schools', value: 'schools' },
    // { label: 'Boys', value: 'boys' },
    // { label: 'Girls', value: 'girls' },
    { label: 'Teachers', value: 'teachers' },
    { label: 'Gender Parity Index', value: 'gpt' },
    { label: 'Pupil-Teacher Ratio', value: 'ptr' },
    { label: 'Avg Students/School', value: 'ass' },
    { label: 'Girls Share %', value: 'gs' },
  ];
  const [metric, setMetric] = useState<MetricKey>('students');

  const metricValue = (r: ComparisonRow): number => {
    switch (metric) {
      case 'gpt':
        return +(r.girls / Math.max(1, r.boys)).toFixed(2);
      case 'ptr':
        return Math.round(r.students / Math.max(1, r.teachers));
      case 'ass':
        return Math.round(r.students / Math.max(1, r.schools ?? 1));
      case 'gs':
        return +((r.girls / Math.max(1, r.students)) * 100).toFixed(1);
      default:
        return Number(r[metric] ?? 0);
    }
  };

  const [viewBlock, setViewBlock] = useState<string | null>(null);
  // Reset the local viewing block whenever the drilled district/block scope
  // changes, so a stale block name never lingers in the context bar.
  React.useEffect(() => {
    setViewBlock(null);
  }, [district, block, level]);

  const levelNoun =
    level === 3 ? 'Schools' : level === 2 ? 'Blocks' : 'Districts';

  // When a school is selected in the header, narrow the rows to just that one
  // school (level 3) so the ranked bar + indicator table show a single entity
  // in sync with the selection. Matches by school_id first, else by the bare
  // name (the row name carries a trailing " (udise)" which is stripped).
  const effectiveRows = useMemo(() => {
    if (level !== 3 || !school) return rows;
    const want = school.trim().toLowerCase();
    const stripUdise = (n: string) =>
      n
        .replace(/\s*\(\d[\d/]*\)\s*$/, '')
        .trim()
        .toLowerCase();
    const filtered = rows.filter((r) => {
      const id = (r as Record<string, unknown>).school_id;
      if (schoolId != null && id != null && String(id) === String(schoolId))
        return true;
      return stripUdise(String(r.name ?? '')) === want;
    });
    // Fall back to the full list if nothing matched (so the view is never
    // empty due to an id/name mismatch).
    return filtered.length > 0 ? filtered : rows;
  }, [rows, level, school, schoolId]);

  // Schools (level 3) are the deepest comparison level — neither the ranked
  // bar chart nor the indicator table drills any further there. This guard is
  // applied to both the chart's onBarClick and the table's onRowClick below so
  // school rows/bars are inert (no click handler, no pointer affordance).
  const isSchoolLevel = level === 3;

  // "Currently viewing" context — the deepest selection wins. A locally-
  // selected block (clicked in the Blocks-level table, no drill) takes
  // precedence over the drilled block/district so the bar reflects the row the
  // user just clicked without changing the chart.
  const viewingName =
    (viewBlock && viewBlock.trim()) ||
    (block && block.trim()) ||
    (district && district.trim()) ||
    '';
  const viewingBackLabel = viewBlock
    ? `Back to ${district ?? ''} — Blocks`
    : block
      ? `Back to ${district ?? ''} — Blocks`
      : district
        ? 'Back to Tamil Nadu — Districts'
        : '';
  // Current geography scope for the heading — the deepest selected level,
  // including a locally-clicked block, so the title matches the figures.
  const geoScopeName = geoScopeLabel({ district, block: block ?? viewBlock });

  // Back bar → first clear a local viewing block (returns to the all-blocks
  // view), else clear the deepest drilled level, stepping up one.
  const handleViewingBack = () => {
    if (viewBlock) setViewBlock(null);
    else if (block) onBlockChange?.('');
    else if (district) onDistrictChange?.('');
  };

  // Ranked bar chart data (desc), with per-bar color vs the average.
  const { labels, series, avg } = useMemo(() => {
    const sorted = [...effectiveRows].sort(
      (a, b) => metricValue(b) - metricValue(a)
    );
    const values = sorted.map((r) => metricValue(r));
    const average = values.reduce((s, v) => s + v, 0) / (values.length || 1);
    const colors = values.map((v) => (v >= average ? '#2f5fb0' : '#f0803c'));
    return {
      labels: sorted.map((r) => r.name),
      avg: average,
      series: [
        {
          label: metrics.find((m) => m.value === metric)?.label ?? 'Value',
          data: values,
          color: '#2f5fb0',
          colors,
        },
      ] as ChartSeries[],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metric, effectiveRows]);

  // Format a student/boys/girls count: below 1 lakh → the full Indian-formatted
  // count (e.g. "99,999"); 1 lakh and above → lakhs ("10.8 L"). Schools /
  // teachers always stay as plain Indian-formatted counts.
  const formatMetricValue = (v: number) => {
    // Derived indicators get their own units.
    if (metric === 'gpt') return v.toFixed(2);
    if (metric === 'ptr') return `${Math.round(v)}:1`;
    if (metric === 'gs') return `${v.toFixed(1)}%`;
    if (metric === 'ass') return v.toLocaleString('en-IN');
    if (metric === 'schools' || metric === 'teachers') {
      return v.toLocaleString('en-IN');
    }
    return v < 100000
      ? v.toLocaleString('en-IN')
      : `${(v / 100000).toFixed(1)} L`;
  };

  // Indicator table: derive GPI, PTR, Avg/School, Girls % from the API rows.
  const indicatorRows = useMemo(
    () =>
      [...effectiveRows]
        .sort((a, b) => b.students - a.students)
        .map((r) => ({
          ...r,
          name: r.name,
          schools: r.schools ?? 0,
          students: r.students,
          gpi: +(r.girls / Math.max(1, r.boys)).toFixed(2),
          ptr: `${Math.round(r.students / Math.max(1, r.teachers))}:1`,
          avgSchool: Math.round(r.students / Math.max(1, r.schools ?? 1)),
          girlsPct: `${((r.girls / Math.max(1, r.students)) * 100).toFixed(1)}%`,
        })),
    [effectiveRows]
  );

  // Columns — hide the Schools/Avg-School columns at school level (a single
  // school is always 1 school).
  const indicatorColumns: ReportColumn[] = [
    // Districts (level 1) drill → blocks; blocks (level 2) select themselves
    // in the header. Schools (level 3) are inert, so plain text.
    {
      // 'link' at every level: districts drill to blocks, blocks select
      // themselves, a school row (the leaf) opens its School Detail view.
      key: 'name',
      label: drillLevelLabel(levelNoun),
      type: 'link',
    },
    ...(level === 3
      ? []
      : [{ key: 'schools', label: 'Schools', type: 'number' } as ReportColumn]),
    { key: 'students', label: 'Students', type: 'number', bold: true },
    { key: 'gpi', label: 'GPI', type: 'number' },
    { key: 'ptr', label: 'PTR', type: 'text' },
    ...(level === 3
      ? []
      : [
          {
            key: 'avgSchool',
            label: 'Avg/School',
            type: 'number',
          } as ReportColumn,
        ]),
    { key: 'girlsPct', label: 'Girls %', type: 'text' },
  ];

  // State/scope-average summary line under the indicator title. All three are
  // true aggregates over the API rows (sum the raw counts, then divide) rather
  // than an average-of-ratios, so they match the headline totals:
  //   GPI = Σgirls / Σboys, PTR = Σstudents / Σteachers,
  //   avg/school = Σstudents / Σschools.
  const safeRows = effectiveRows.length > 0 ? effectiveRows : [];
  const totalBoys = safeRows.reduce((s, r) => s + r.boys, 0);
  const totalGirls = safeRows.reduce((s, r) => s + r.girls, 0);
  const totalStudents = safeRows.reduce((s, r) => s + r.students, 0);
  const totalTeachers = safeRows.reduce((s, r) => s + r.teachers, 0);
  const totalSchools = safeRows.reduce((s, r) => s + (r.schools ?? 0), 0);
  const stateGpi = (totalGirls / Math.max(1, totalBoys)).toFixed(2);
  const statePtr = Math.round(totalStudents / Math.max(1, totalTeachers));
  const stateAvgSchool = Math.round(totalStudents / Math.max(1, totalSchools));

  const metricLabel = metrics.find((m) => m.value === metric)?.label ?? '';

  // Ranked (horizontal bar) chart height scales with the number of bars: a few
  // blocks/schools stay compact (no giant stretched bars), many districts /
  // schools get enough room to stay readable — clamped to a sensible range.
  // ~34px per bar + padding; AppChart's generic autoHeight is turned off since
  // we size it precisely here.

  const SCROLL_BAR_THRESHOLD = 13;
  const rankedHeight =
    labels.length > SCROLL_BAR_THRESHOLD
      ? 520
      : Math.min(1100, Math.max(260, labels.length * 34 + 64));

  return (
    <div className="comparison">
      {/* ── Header ── */}
      <div className="comparison__head dash-section">
        <FontAwesomeIcon icon={faCodeCompare} className="comparison__icon" />
        <h5 className="comparison__title">
          {scopeLabel} — {geoScopeName}
          {year && <span className="comparison__subtitle"> {year}</span>}
        </h5>
      </div>

      {/* ── Currently-viewing context bar (mirrors the Numbers tab) ── */}
      {/* {viewingName && (
        <div className="comparison__viewing-bar dash-section">
          <button
            type="button"
            className="comparison__viewing-back"
            onClick={handleViewingBack}
          >
            <i className="pi pi-arrow-left" /> {viewingBackLabel}
          </button>
          <span className="comparison__viewing">
            Currently viewing: <strong>{viewingName}</strong>
          </span>
          <span className="comparison__viewing-home">
            <i className="pi pi-home" /> Tamil Nadu
          </span>
        </div>
      )} */}

      {/* ── Ranked bar chart ── */}
      <div className="dash-section">
        <AppChart
          type="horizontalBar"
          title={`Ranked by ${metricLabel}${
            level === 1 ? ' — click a bar to drill down' : ''
          }`}
          icon="fa-solid fa-chart-simple"
          labels={labels}
          series={series}
          height={rankedHeight}
          autoHeight={false}
          averageLine={avg}
          barValueFormat={formatMetricValue}
          onBarClick={
            // Districts only → blocks. Block level does NOT drill to schools
            // (commented out: comparison stays at district/block level).
            // Schools (level 3) never drill from the ranked chart.
            !onRowClick
              ? undefined
              : (index) => {
                  const name = labels[index];
                  const row = rows.find((r) => r.name === name);
                  if (row) onRowClick(row as Record<string, unknown>);
                }
          }
          headerRight={
            <Dropdown
              value={metric}
              options={metrics}
              onChange={(e) => setMetric(e.value)}
              className="comparison__metric"
              panelClassName="comparison__metric-panel"
            />
          }
        />
        <p className="comparison__avg-note">
          Dashed reference = {levelNoun.toLowerCase()} average ({metricLabel}):{' '}
          <strong>{formatMetricValue(avg)}</strong> · blue = above average,
          orange = below
        </p>
      </div>

      {/* ── Indicator comparison table ── */}
      <div className="dash-section">
        {/* `viewBlock` is a local row-click selection that sits below the
            drilled header block, so it shows as the deepest crumb and Back
            clears it first (handleViewingBack already does that). */}
        <DrillBreadcrumb
          district={district}
          block={block ?? viewBlock}
          school={block ? viewBlock : null}
          onBack={handleViewingBack}
          onDistrictChange={onDistrictChange}
          onBlockChange={(v) => {
            setViewBlock(null);
            onBlockChange?.(v);
          }}
          onSchoolChange={() => setViewBlock(null)}
        />
        <ReportDataTable
          title={`Indicator Comparison — ${levelNoun}`}
          // Averages note, plus what a row click does at this level: districts
          // drill to blocks, blocks select themselves, schools are inert.
          hint={`averages — GPI ${stateGpi}, PTR ${statePtr}:1, ${stateAvgSchool} students/school ${drillHint(
            levelNoun,
            level === 2
          )}`.trim()}
          icon="fa-solid fa-table-list"
          data={indicatorRows as unknown as Record<string, unknown>[]}
          // columns={indicatorColumns}
          columns={
            level === 3 && indicatorRows.length === 1
              ? [
                  { ...indicatorColumns[0], type: 'text' },
                  ...indicatorColumns.slice(1),
                ]
              : indicatorColumns
          }
          exportFileName="indicator-comparison"
          searchPlaceholder={drillSearchPlaceholder(levelNoun)}
          rows={12}
          // Districts → drill to blocks. Blocks → set the clicked block in the
          // header Block dropdown (value box) AND show it in the "Currently
          // viewing" bar. Schools (level 3) → fully inert, no handler at all.
          onRowClick={
            isSchoolLevel
              ? onRowClick // school leaf → select the school (narrows via `school`)
              : level === 1
                ? onRowClick
                : level === 2
                  ? (row) => {
                      const name = String(row.name ?? '') || null;
                      setViewBlock(name);
                      // Populate the header Block dropdown with the selection.
                      if (name) onBlockChange?.(name);
                    }
                  : undefined
          }
        />
      </div>
    </div>
  );
};

export default Comparison;
